import { useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react'
import { type InertiaFormProps } from '@inertiajs/react'

import { Button } from '@atoms'
import Alert from '@components/atoms/Alert'
import Badge from '@components/atoms/Badge'
import { t } from '@utils/I18n'

export const fieldErrors = (errors: Record<string, string>) =>
  Object.fromEntries(Object.entries(errors).map(([field, message]) => [field, { type: 'server', message }]))

// The name, value, onChange and errors a TextInput needs for one setting.
export const textField = <T extends Record<string, string>>(form: InertiaFormProps<T>, name: keyof T & string) => ({
  name,
  value: form.data[name],
  onChange: (event: ChangeEvent<HTMLInputElement>) => form.setData((data) => ({ ...data, [name]: event.target.value })),
  errors: fieldErrors(form.errors),
})

type SettingsFormFooterProps = { disabled: boolean; saved: boolean }

export function SettingsFormFooter({ disabled, saved }: SettingsFormFooterProps) {
  return (
    <div
      data-slot="settings-form-footer"
      className="tw:flex tw:items-center tw:justify-end tw:gap-3 tw:rounded-b-xl tw:border-t tw:border-gray-cool-200 tw:bg-gray-cool-50 tw:px-5 tw:py-3"
    >
      <div role="status">
        {saved && (
          <Badge color="success" leadingIcon="check">
            {t('configurations.saved')}
          </Badge>
        )}
      </div>
      <Button type="submit" disabled={disabled}>
        {t('shared.buttons.save')}
      </Button>
    </div>
  )
}

export type SettingsFormProps<T extends Record<string, unknown>> = {
  form: InertiaFormProps<T>
  action: string
  children: ReactNode
}

// A provider's answer to a login just saved, from SectionController#check_connection.
type Connection = { connected: boolean; message: string }

export function SettingsForm<T extends Record<string, unknown>>({ form, action, children }: SettingsFormProps<T>) {
  const saved = useRef(form.data)
  // Kept from the save's own response: page flash is cleared by the next visit, the sidebar's poll included.
  const [connection, setConnection] = useState<Connection>()
  const submit = (event: FormEvent) => {
    event.preventDefault()
    setConnection(undefined)
    form.transform((data) => ({
      settings: Object.fromEntries(Object.entries(data).filter(([key, value]) => value !== saved.current[key])),
    }))
    form.patch(action, {
      preserveScroll: true,
      onSuccess: (page) => {
        saved.current = form.data
        form.setDefaults()
        setConnection(page.flash.connection as Connection | undefined)
      },
    })
  }

  return (
    <form onSubmit={submit}>
      <div className="tw:flex tw:flex-col tw:gap-5 tw:p-5">
        {children}
        <div role="status">
          {connection && (
            <Alert type={connection.connected ? 'success' : 'error'} size="sm">
              {connection.message}
            </Alert>
          )}
        </div>
      </div>
      <SettingsFormFooter disabled={!form.isDirty || form.processing} saved={form.wasSuccessful && !form.isDirty} />
    </form>
  )
}
